import { Toggle, Text, Icon, Tooltip, Flex } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import T from 'Components/Helpers/translate';
import { useSelector } from 'react-redux';
import { RootState } from 'Store/index';
import React, { useEffect } from 'react';
import { useSoftDeployAccess, useFeatureAvailable } from 'App/components/hooks';
import { Feature } from 'Store/actions/FeatureHelperActions';
import { IntegrationConfig } from 'App/resources/AllIWant';
import './InvoiceRelatedClient.scss';

type OperationalAccount = {
	id: number;
	name: string;
};

type Props = {
	disabled: boolean;
	checked: boolean;
	onChange: (checked: boolean) => void;
	orderModal?: boolean;
	clientRelated?: boolean;
	operationalAccount?: null | OperationalAccount;
};

const VISMA_ID = 364;
const SUPPORTED_INTEGRATION_IDS = [
	VISMA_ID //Visma.NET
];

const SUPPORTED_INTEGRATION_NAMES = ['fortnox'];

export const hasSupportedIntegration = (activeIntegrations: IntegrationConfig[]) => {
	return activeIntegrations.some(i => {
		const idMatch = SUPPORTED_INTEGRATION_IDS.includes(i.id);
		const nameMatch = SUPPORTED_INTEGRATION_NAMES.some(name => i.name.toLowerCase().includes(name.toLowerCase()));
		return idMatch || nameMatch;
	});
};

const InvoiceRelatedClient = ({
	disabled,
	checked,
	onChange,
	orderModal = false,
	clientRelated,
	operationalAccount = null
}: Props) => {
	const { metadata } = useSelector(({ App }: RootState) => App);
	const hasSubAccountsV2 = useSoftDeployAccess('SUB_ACCOUNTS_V2');
	const hasSubAccountsFeature = useFeatureAvailable(Feature.SUB_ACCOUNTS) && hasSubAccountsV2;
	const hasSubAccountsAndOperationalAccount = hasSubAccountsFeature && operationalAccount !== null;

	const activeIntegrations = metadata?.integrations?.active;
	const isVismaActive = activeIntegrations?.some(i => i.id === VISMA_ID) ?? false;
	const showInvoiceRelatedClient = activeIntegrations && hasSupportedIntegration(activeIntegrations);

	useEffect(() => {
		if (!clientRelated && checked) {
			onChange(false);
		}
	}, [clientRelated]);

	if (!showInvoiceRelatedClient) {
		return null;
	}

	const classes = new BemClass('InvoiceRelatedClient');
	return (
		<div className={classes.mod({ orderModal, isVismaActive, hasSubAccountsAndOperationalAccount }).b()}>
			{isVismaActive ? (
				<Text size="sm" color="grey-11">
					{T('subscription.modal.details.invoiceRelatedClientTooltip')}
				</Text>
			) : hasSubAccountsAndOperationalAccount ? (
				<>
					<Toggle size="md" disabled={disabled} checked={checked} onChange={onChange} />
					<Flex direction="column" gap="u1">
						<Flex direction="row" gap="u1">
							<Text size="md" bold>
								{T('subscription.modal.details.invoiceOperationalAccount')}
							</Text>
							<Tooltip title={T('subscription.modal.details.invoiceOperationalAccountTooltip')}>
								<Icon name="question-circle" />
							</Tooltip>
						</Flex>
						<Text size="sm" color="grey-11">
							{operationalAccount?.name}
						</Text>
					</Flex>
				</>
			) : (
				<>
					<Toggle size="sm" disabled={disabled} checked={checked} onChange={onChange} />
					<Text size="sm" color="grey-11">
						{T('subscription.modal.details.invoiceRelatedClient')}
					</Text>

					<Tooltip title={T('subscription.modal.details.invoiceRelatedClientTooltip')}>
						<Icon name="question-circle" />
					</Tooltip>
				</>
			)}
		</div>
	);
};

export default InvoiceRelatedClient;
